import { useEffect, useMemo, useRef } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import { updateLoadingState } from 'store/app.slice';
import { useAppDispatch, useAppSelector } from 'store/hook';

import { externalFetchAll, internalFetchAll } from 'handlers/util/fetch.all';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  hasAccessToMultipleCustomers,
  isExternalRole,
  isInternalRole,
} from 'utils/validators/role.validation';

// handlers
import ExternalUserHandler from 'handlers/external.user.handler';
import InternalUserHandler from 'handlers/internal.user.handler';
import CustomerHandler from 'handlers/customer.handler';
import AuthHandler from 'handlers/auth.handler';
import AssetHandler from 'handlers/asset.handler';
import OperatorHandler from 'handlers/operator.handler';
import HierarchyHandler from 'handlers/hierarchy.handler';
import RuleHandler from 'handlers/rule.handler';
import DeviceHandler from 'handlers/device.handler';
import TabletHandler from 'handlers/tablet.handler';
import TagHandler from 'handlers/tag.handler';
import SquaresMaintenanceHandler from 'handlers/squaresHandlers/squaresMaintenance.handler';
import SquaresDashboardHandler from 'handlers/squaresHandlers/squaresDashboard.handler';
import NotificationHandler from 'handlers/notification.handler';
import EngineHoursHandler from 'handlers/engine.hours.handler';
import WeatherHandler from 'handlers/weather.handler';
import FuelPriceHandler from 'handlers/fuelPrice.handler';
import FleetOverviewHandler from 'handlers/fleetOverView.handler';
import { useRemoveAuth } from 'hooks/useRemoveAuth';
import { useLanguageSync } from 'hooks/useLanguageSync';
import { useOperatorSessionSwitchSSE } from 'hooks/useOperatorSessionSwitchSSE';
import SurvivalAnalysisOverviewHandler from 'handlers/survivalAnalysisHandlers/survivalAnalysisOverview.handler';

import { ThemePalette } from 'mui.theme';
import UtilizationHandler from 'handlers/utilization.handler';
import ProductionHandler from 'handlers/production.handler';
import TirePressureHandler from 'handlers/tire-pressure';
import TagThresholdHandler from 'handlers/tag.threshold.handler';
import GeoHandler from 'handlers/geo.handler';
// import DtcHandler from 'handlers/dtc.handler';
import TargetHandler from 'handlers/targets.handler';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import MainRouterNew from 'components/Route/MainRouterNew';
import { ErrorBoundary } from 'components/ErrorBoundary/ErrorBoundary';
import MlInsightsHandler from 'handlers/ml-insights.handler';
import TirePatternHandler from 'handlers/tire-patterm.handler';
import { useCurrentUserInfo } from 'hooks/useCurrentUserInfo';
import { SingleFadeLoader } from 'components/SingleFadeLoader';
import NavigationMapHandler from 'handlers/navigation-map.handler';
import { ga4Analytics } from 'services/analytics.service';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const CUSTOMER_SELECTION_EXEMPT_ROUTES = [
  '/customer-selection',
  '/nav-config',
  '/customer-settings',
];

function App(): JSX.Element | null {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  useLanguageSync();
  useOperatorSessionSwitchSSE();
  const { isAuthenticated, isLoading } = useAuth0();
  const { execRemoveAuth } = useRemoveAuth();
  const currentUser = useCurrentUserInfo();
  const {
    isUserInfoLoading,
    accessToken,
    role,
    customer: { theme, modules },
  } = useAppSelector((state) => state.authReducer);
  const customer = useAppSelector((state) => state.persistedReducer.customer);
  const fetchedForCustomerRef = useRef<string | null>(null);
  const palette =
    theme === 'dark'
      ? clientThemePalette[CLIENT_THEME].dark
      : clientThemePalette[CLIENT_THEME].light;

  // handlers - add new handlers here
  const authHandler = new AuthHandler();
  const internalUserHandler = new InternalUserHandler();
  const customerHandler = new CustomerHandler();
  const externalUserHandler = new ExternalUserHandler();
  const assetHandler = new AssetHandler();
  const engineHoursHandler = new EngineHoursHandler();
  const operatorHandler = new OperatorHandler();
  const hierarchyHandler = new HierarchyHandler();
  const ruleHandler = new RuleHandler();
  const deviceHandler = new DeviceHandler();
  const tabletHandler = new TabletHandler();
  const tagHandler = new TagHandler();
  const squaresMaintenanceHandler = new SquaresMaintenanceHandler();
  const squaresDashboardHandler = new SquaresDashboardHandler();
  const notificationHandler = new NotificationHandler();
  const weatherHandler = new WeatherHandler();
  const fuelPriceHandler = new FuelPriceHandler();
  const fleetOverviewHandler = new FleetOverviewHandler();
  const survivalAnalysisOverviewHandler = new SurvivalAnalysisOverviewHandler();
  const utilizationHandler = new UtilizationHandler();
  const productionHandler = new ProductionHandler();
  const tirePressureHandler = new TirePressureHandler();
  const tagThresholdHandler = new TagThresholdHandler();
  const geoHandler = new GeoHandler();
  // const dtcHandler = new DtcHandler(); // not needed added dtc fetching fetching with caching directly in notification bell
  const targetHandler = new TargetHandler();
  const mlInsightsHandler = new MlInsightsHandler();
  const tirePatternHandler = new TirePatternHandler();
  const navigationMapHandler = new NavigationMapHandler();

  const externalFetchHandlers = {
    handlers: {
      externalUserHandler,
      assetHandler,
      operatorHandler,
      hierarchyHandler,
      ruleHandler,
      deviceHandler,
      tabletHandler,
      tagHandler,
      squaresMaintenanceHandler,
      squaresDashboardHandler,
      notificationHandler,
      engineHoursHandler,
      weatherHandler,
      fuelPriceHandler,
      fleetOverviewHandler,
      survivalAnalysisOverviewHandler,
      utilizationHandler,
      productionHandler,
      tirePressureHandler,
      tagThresholdHandler,
      geoHandler,
      // dtcHandler,
      targetHandler,
      mlInsightsHandler,
      tirePatternHandler,
      modules,
    },
    customerId: customer.id,
  };

  const internalFetchHandlers = {
    handlers: {
      internalUserHandler,
      customerHandler,
      ...externalFetchHandlers.handlers,
    },
  };

  /**
   * Determines if the current customer is part of the user’s customer associations.
   */
  const isCustomerAssociated = useMemo(() => {
    if (role && isInternalRole(role)) return true;
    if (!Array.isArray(currentUser?.userCustomerAssociations)) return false;
    return currentUser?.userCustomerAssociations?.some(
      (el) => el?.customerId === customer?.id
    );
  }, [customer?.id, currentUser?.userCustomerAssociations, role]);

  useEffect(() => {
    // if authenticated or loading, return
    if ((isAuthenticated && !isLoading) || isLoading) return;

    void authHandler.login();

    dispatch(updateLoadingState(true));
  }, [isAuthenticated, isLoading]);

  useEffect(() => {
    // Return early if token has been set
    if (isLoading || accessToken?.length > 0) return;

    void authHandler.hydrateToken();
    void authHandler.hydrateClaims();

    dispatch(updateLoadingState(false));
  }, [isLoading]);

  useEffect(() => {
    let backgroundColor = palette.pageBackground;
    if (theme === 'dark') {
      backgroundColor = palette.pageBackground;
    }
    document.body.style.backgroundColor = backgroundColor;
  }, [theme]);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) return;
    void internalUserHandler.userInfoSync();
  }, [isAuthenticated, accessToken]);

  /**
   * GA4 Analytics Initialization
   * Identifies the user and customer context for event tracking.
   */
  useEffect(() => {
    if (!isAuthenticated || !accessToken || isUserInfoLoading || !role) return;
    if (!customer?.code || !currentUser) return;

    ga4Analytics.initialize({
      userId: (currentUser as any)?.userId || '',
      userEmail: (currentUser as any)?.email || '',
      userRole: role,
      customerCode: customer.code,
      customerId: customer.id,
    });
  }, [
    isAuthenticated,
    accessToken,
    isUserInfoLoading,
    role,
    customer?.code,
    currentUser,
  ]);

  /*
    Execute fetch all if authenticated and access token is set
    handlers must be instantiated before being passed to fetchAll
    to establish context for redux dispatch and other hooks
    this is because hooks cannot be called outside a functional component
  */
  useEffect(() => {
    if (!isAuthenticated || !accessToken || !role || isUserInfoLoading) {
      return;
    }

    const isExemptFromCustomerSelectionRedirect =
      CUSTOMER_SELECTION_EXEMPT_ROUTES.includes(location.pathname);

    // Navigate internal user to the customer selection screen
    // if a customer has not been assigned to app context. Routes reachable
    // directly from that screen before a customer is picked (nav config,
    // customer settings) are exempt, otherwise this effect -- which re-runs
    // on every route change -- immediately bounces the user right back.
    if (
      !customer.id &&
      hasAccessToMultipleCustomers(role) &&
      !isExemptFromCustomerSelectionRedirect
    ) {
      navigate('/customer-selection');
      return;
    }

    // checks for customer lvl access in case of multiple sites
    if (hasAccessToMultipleCustomers(role)) {
      if (!isCustomerAssociated && !isExemptFromCustomerSelectionRedirect) {
        navigate('/customer-selection');
        return;
      }
    }

    // /modules is the pre-module-selection screen -- nothing rendered there
    // needs fleet/asset/rule/device data, so don't fetch it until the user
    // actually navigates into a module. location.pathname is read here as a
    // captured closure value, not a dependency, so this effect doesn't
    // re-run -- and re-fetch everything -- on every route change once the
    // user leaves /modules.
    if (location.pathname === '/modules') {
      return;
    }

    // customer.id can be transiently empty mid customer-switch (resetApp()
    // dispatches RESET_STORE before the new customer is written back in),
    // which would otherwise slip past the guard below since '' !== the
    if (!customer.id && hasAccessToMultipleCustomers(role)) {
      return;
    }

    // Fetch at most once per authenticated session per customer. Without
    // this guard, the effect fires once while the router is still on the
    // pre-redirect path and again after LoginView's <Navigate> resolves,
    // double-firing every handler in fetch.all.ts on every login.
    if (fetchedForCustomerRef.current === customer.id) {
      return;
    }
    fetchedForCustomerRef.current = customer.id;

    void navigationMapHandler.getNavigationMap(true);

    if (isExternalRole(role)) {
      void Promise.allSettled([externalFetchAll(externalFetchHandlers)]);
      return;
    }

    // Fetch information for internal users
    void Promise.allSettled([internalFetchAll(internalFetchHandlers)]);
  }, [
    isAuthenticated,
    accessToken,
    role,
    customer?.id,
    isCustomerAssociated,
    isUserInfoLoading,
    // Needed so this effect re-evaluates once the user navigates away from
    // /modules (the pathnameRef check above reads the live value, but the
    // effect itself only re-runs when a dependency changes). The
    // fetchedForCustomerRef guard above stops this from causing a second
    // fetch once one has already run for the current customer.
    location.pathname,
  ]);

  useEffect(() => {
    let timeout: any;
    const keyToDelete = [
      'production_cache_1D_all_daily',
      'utilization_cache_1D_daily_false_1_engineHours',
    ];
    keyToDelete.forEach((key) => {
      sessionStorage.removeItem(key);
    });
    // Iterate backwards over all keys in sessionStorage to avoid skipping items
    // when removing (since removal causes remaining items to shift)
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const key = sessionStorage.key(i);
      // Clear the key if it's not 'persist:root'
      if (key !== 'persist:root' && key != null) {
        sessionStorage.removeItem(key);
      }
    }

    const resetTimeout = () => {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => {
        // Prevent auto-logout when on the control-room page
        if (window.location.pathname === '/control-room') return;
        execRemoveAuth();
      }, 3600000); // 1 hour in milliseconds
    };

    const handleActivity = () => {
      resetTimeout();
    };

    // Reset the timeout on user activity
    document.addEventListener('mousemove', handleActivity);
    document.addEventListener('keydown', handleActivity);

    // Initial setup
    resetTimeout();

    // Clean up event listeners
    return () => {
      document.removeEventListener('mousemove', handleActivity);
      document.removeEventListener('keydown', handleActivity);
      if (timeout) clearTimeout(timeout);
    };
  }, []);

  /* if loading, show loading screen */
  if (isLoading) {
    dispatch(updateLoadingState(true));
    return null;
  }

  if (isUserInfoLoading) {
    return <SingleFadeLoader isFullScreen={true} />;
  }

  return (
    <ErrorBoundary>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MainRouterNew />
      </LocalizationProvider>
    </ErrorBoundary>
  );
}

export default App;
